import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Users } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { apiError, type Segment, type SegmentVocabulary } from './lib';
import SegmentFormDialog from './segments/SegmentFormDialog';
import SegmentMembersDialog from './segments/SegmentMembersDialog';

/**
 * P9 / ADR-0010 §3 — saved, re-evaluated audiences. Deliberately NOT a
 * free-text query builder: every rule is a field and operator off
 * `GET /segments/vocabulary`, never a string that reaches SQL.
 *
 * Polls rather than subscribes: `customer_segment.evaluated` and
 * `customer.merged` are emitted (`event-names.ts`) but neither is forwarded by
 * `RealtimeGateway` (`realtime.service.ts` has no `@OnEvent` for either), and
 * membership is materialised on an hourly sweep in any case — a socket
 * listener here would sit open for nothing.
 */
export default function SegmentsTab() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm().confirm;
  const canCreate = can('segments.create');
  const canUpdate = can('segments.update');
  const canDelete = can('segments.delete');

  const [rows, setRows] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [vocabulary, setVocabulary] = useState<SegmentVocabulary | null>(null);
  const [vocabError, setVocabError] = useState<string | null>(null);

  const [form, setForm] = useState<{ open: boolean; segment: Segment | null }>({
    open: false,
    segment: null,
  });
  const [membersOf, setMembersOf] = useState<Segment | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: Segment[] }>('/segments', { params: { limit: 100, search: search || undefined } })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('crm.segments.loadFailed'))))
      .finally(() => setLoading(false));
  }, [search, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    api
      .get<SegmentVocabulary>('/segments/vocabulary')
      .then(({ data }) => setVocabulary(data))
      .catch((e) => setVocabError(apiError(e, t('crm.segments.vocabularyLoadFailed'))));
  }, [t]);

  const openCreate = () => {
    if (!vocabulary) return;
    setForm({ open: true, segment: null });
  };

  const remove = async (segment: Segment) => {
    const ok = await confirm({
      title: t('crm.segments.deleteConfirmTitle'),
      body: t('crm.segments.deleteConfirmBody', { name: segment.name }),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/segments/${segment.id}`);
      toast.success(t('crm.segments.deleted'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('crm.segments.deleteFailed')));
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <div>
          <CardTitle>{t('crm.segments.title')}</CardTitle>
          <p className="text-sm text-ink-muted">{t('crm.segments.subtitle')}</p>
        </div>
        {canCreate && (
          <Button size="sm" onClick={openCreate} disabled={!vocabulary}>
            <Icon as={Plus} size="sm" className="me-1.5" />
            {t('crm.segments.newSegment')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {vocabError && !vocabulary && <ErrorState message={vocabError} />}

        <Input
          placeholder={t('crm.segments.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (
          <LoadingState label={t('common.loading')} />
        ) : error ? (
          <ErrorState message={error} onRetry={load} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={t('crm.segments.empty')}
            hint={t(search ? 'crm.segments.emptySearchHint' : 'crm.segments.emptyHint')}
            action={
              !search && canCreate && vocabulary ? (
                <Button size="sm" onClick={openCreate}>
                  {t('crm.segments.newSegment')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('crm.segments.name')}</TH>
                  <TH>{t('crm.segments.freshness')}</TH>
                  <TH className="text-end">{t('crm.segments.memberCount')}</TH>
                  <TH>{t('crm.segments.status')}</TH>
                  <TH />
                </TR>
              </THead>
              <tbody>
                {rows.map((segment) => (
                  <TR key={segment.id}>
                    <TD className="font-medium">
                      <button
                        type="button"
                        className="text-start hover:underline"
                        onClick={() => setMembersOf(segment)}
                      >
                        {segment.name}
                      </button>
                      {segment.description && (
                        <p className="text-xs text-ink-muted">{segment.description}</p>
                      )}
                    </TD>
                    <TD>
                      {segment.neverEvaluated ? (
                        <Badge tone="neutral">{t('crm.segments.neverEvaluatedBadge')}</Badge>
                      ) : segment.lastEvaluationError ? (
                        <Badge tone="danger">{t('crm.segments.evaluationError')}</Badge>
                      ) : (
                        <Badge tone={segment.stale ? 'warning' : 'success'}>
                          {t(segment.stale ? 'crm.segments.stale' : 'crm.segments.fresh')}
                        </Badge>
                      )}
                      {segment.lastEvaluatedAt && (
                        <p className="text-xs text-ink-muted">
                          {formatDateTime(segment.lastEvaluatedAt, i18n.language)}
                        </p>
                      )}
                    </TD>
                    <TD className="text-end tabular-nums">
                      {segment.neverEvaluated ? '—' : segment.memberCount}
                    </TD>
                    <TD>
                      <Badge tone={segment.isActive ? 'success' : 'neutral'}>
                        {t(segment.isActive ? 'crm.segments.active' : 'crm.segments.inactive')}
                      </Badge>
                    </TD>
                    <TD className="text-end whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setMembersOf(segment)}>
                        <Icon as={Users} size="sm" className="me-1.5" />
                        {t('crm.segments.viewMembers')}
                      </Button>
                      {canUpdate && vocabulary && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setForm({ open: true, segment })}
                        >
                          {t('common.edit')}
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => remove(segment)}>
                          {t('common.delete')}
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </CardContent>

      {form.open && vocabulary && (
        <SegmentFormDialog
          vocabulary={vocabulary}
          segment={form.segment}
          onClose={() => setForm({ open: false, segment: null })}
          onSaved={() => {
            setForm({ open: false, segment: null });
            load();
          }}
        />
      )}

      {membersOf && (
        <SegmentMembersDialog
          segment={membersOf}
          onClose={() => setMembersOf(null)}
          onChanged={load}
        />
      )}
    </Card>
  );
}
